// src/pages/InventoryCounting/components/ImportStatusCards.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getInventoryImports } from '../../../../api/inventoryCountingApi';

const TYPES = [
  { type: 'STOCK', label: 'Zapas' },
  { type: 'DISPATCH', label: 'Wywozy' },
  { type: 'COUNTING', label: 'Liczenia' },
];

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL');
}

export default function ImportStatusCards({ refreshKey }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadImports = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getInventoryImports();
      setImports(result.rows || []);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Nie udało się pobrać statusu importów.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadImports();
  }, [refreshKey]);

  const latestByType = useMemo(() => {
    const map = {};

    TYPES.forEach((item) => {
      map[item.type] = null;
    });

    imports.forEach((row) => {
      if (!map[row.type]) {
        map[row.type] = row;
      }
    });

    return map;
  }, [imports]);

  const missingTypes = TYPES.filter((item) => !latestByType[item.type]);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Status danych
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {missingTypes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Brakuje importu: {missingTypes.map((x) => x.label).join(', ')}.
          Rekomendacje mogą być niepełne.
        </Alert>
      )}

      {TYPES.map((item) => {
        const latest = latestByType[item.type];

        if (latest?.status !== 'FAILED') return null;

        return (
          <Alert severity="error" sx={{ mb: 2 }} key={item.type}>
            Ostatni import <strong>{item.label}</strong> zakończył się błędem:{' '}
            {latest.errorMessage || 'Brak szczegółów błędu.'}
          </Alert>
        );
      })}

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {TYPES.map((item) => {
            const latest = latestByType[item.type];

            return (
              <Grid item xs={12} md={4} key={item.type}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          {item.label}
                        </Typography>

                        <Chip
                          size="small"
                          label={latest ? latest.status : 'BRAK'}
                          color={
                            latest?.status === 'IMPORTED'
                              ? 'success'
                              : latest?.status === 'FAILED'
                              ? 'error'
                              : latest
                              ? 'default'
                              : 'warning'
                          }
                        />
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        Plik: {latest?.fileName || '-'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Rekordy: {latest?.rowsCount ?? '-'}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Import: {formatDate(latest?.importedAt)}
                      </Typography>
                      {latest?.errorMessage && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={latest.errorMessage}
                        >
                          Błąd: {latest.errorMessage}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Użytkownik:{' '}
                        {latest?.importedBy
                          ? `${latest.importedBy.firstName || ''} ${
                              latest.importedBy.lastName || ''
                            }`.trim()
                          : `${latest?.firstName || ''} ${
                              latest?.lastName || ''
                            }`.trim() || '-'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
